import type { DocumentContext } from '@/pages/KnowledgeBase/KnowledgeBasePage';
import type { KBFolderInfo } from '@/services/kbService';

export interface FolderNode {
  name: string;
  /** Normalised path, e.g. `/contracts/2024/`. */
  path: string;
  /** Documents in this folder and all its subfolders. */
  documentCount: number;
  children: FolderNode[];
}

export const ROOT_FOLDER_PATH = '/';

export function folderPathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

function pathFromSegments(segments: string[]): string {
  return segments.length === 0 ? ROOT_FOLDER_PATH : `/${segments.join('/')}/`;
}

export function parentFolderPath(path: string): string {
  return pathFromSegments(folderPathSegments(path).slice(0, -1));
}

/** Paths of every ancestor of `path`, root excluded, outermost first. */
export function ancestorFolderPaths(path: string): string[] {
  const segments = folderPathSegments(path);
  return segments.slice(0, -1).map((_, index) => pathFromSegments(segments.slice(0, index + 1)));
}

/**
 * Builds the folder tree of a context. Only folders that hold documents are listed by the
 * backend, so missing parents (`/a/` when only `/a/b/` holds files) are created here.
 */
export function buildFolderTree(folders: KBFolderInfo[] | undefined): FolderNode[] {
  const root: FolderNode = { name: '', path: ROOT_FOLDER_PATH, documentCount: 0, children: [] };

  (folders ?? []).forEach((folder) => {
    let node = root;
    folderPathSegments(folder.path).forEach((segment, index, segments) => {
      let child = node.children.find((candidate) => candidate.name === segment);
      if (!child) {
        child = { name: segment, path: pathFromSegments(segments.slice(0, index + 1)), documentCount: 0, children: [] };
        node.children.push(child);
      }
      child.documentCount += folder.document_count;
      node = child;
    });
  });

  const sortTree = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((node) => sortTree(node.children));
  };
  sortTree(root.children);
  return root.children;
}

/** Direct subfolders of `parentPath` (root when `/`). */
export function childFolders(folders: KBFolderInfo[] | undefined, parentPath: string): FolderNode[] {
  let nodes = buildFolderTree(folders);
  for (const segment of folderPathSegments(parentPath)) {
    const next = nodes.find((node) => node.name === segment);
    if (!next) return [];
    nodes = next.children;
  }
  return nodes;
}

export const folderNodeKey = (context: Pick<DocumentContext, 'type' | 'id'>, path: string) =>
  `${context.type}:${context.id}:${path}`;

/** Context of a subfolder of a domain, use case or solution (`context` is the item's root context). */
export const folderContext = (context: DocumentContext, folderPath: string): DocumentContext => {
  const segments = folderPathSegments(folderPath);
  if (segments.length === 0) {
    return { type: context.type, id: context.id, name: context.name, path: context.path };
  }
  return {
    type: context.type,
    id: context.id,
    name: segments[segments.length - 1],
    path: [...context.path, ...segments],
    folderPath,
  };
};
